import React, { FC, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Autocomplete, Box, Divider, Paper, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { labsForAdminTemplate } from 'apollo/templates/lab';
import {
    ApiClientsTable,
    OpenApiDocsButton,
    WebhookDeliveries,
    WebhookSubscriptions,
} from 'components/shared/api-client-management';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Lab } from 'models/lab';

const LAB_FETCH_LIMIT = 500;

const ApiClientSettings: FC = () => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [selectedLab, setSelectedLab] = useState<Lab | null>(null);

    const { data: labsData, loading: labsLoading } = useQuery<{ labsForAdmin: Lab[] }>(labsForAdminTemplate, {
        variables: {
            filters: { freeText: '', active: true },
            limit: LAB_FETCH_LIMIT,
            offset: 0,
            orderBy: 'name',
            orderDirection: 'asc',
        },
        onError: showBoundary,
    });
    const labs = labsData?.labsForAdmin ?? [];

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h1" gutterBottom>
                {translate('app-settings:API_CLIENTS_AND_WEBHOOKS')}
            </Typography>

            <Box display="flex" gap={2} alignItems="center" mb={3}>
                <Autocomplete
                    sx={{ minWidth: 320 }}
                    loading={labsLoading}
                    options={labs}
                    value={selectedLab}
                    onChange={(_, value) => setSelectedLab(value)}
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderInput={(params) => <TextField {...params} label={translate('app-settings:SELECT_LAB')} />}
                />
                <OpenApiDocsButton
                    organization={selectedLab?._id ? { _id: selectedLab._id, name: selectedLab.name ?? '' } : null}
                />
            </Box>

            <ApiClientsTable organization={selectedLab} />

            <Divider sx={{ my: 4 }} />

            <WebhookSubscriptions organization={selectedLab} />

            <Divider sx={{ my: 4 }} />

            <WebhookDeliveries organization={selectedLab} />
        </Paper>
    );
};

export default ApiClientSettings;
